import { useMsal } from '@azure/msal-react';
import React, { useContext, useEffect } from 'react';
import { AmplitudeEventType } from '../../../analytics/amplitudeEvents';
import { type ClientConfig } from '../../../clientConfig';
import { OPENID_PARAMS } from '../../../types/openid_enum';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { getValidatedRedirectUrl } from '../../../utils/redirectValidation';
import { ClientConfigContext } from '../../clientConfigContext';
import { useAccessToken } from '../../hooks/useAccessToken';
import { useValidatedUrl } from '../../hooks/useValidatedUrl';
import { ApiRequestLogger } from '../../logger/apiRequestLogger';
import { buildIdVerificationEventProps, buildPhoneNumberEventProps, sendEvent } from '../../services/amplitudeClient';
import { useErrorLogging } from '../../util/errorUtils';
import { redirectWithSoftLogout } from '../../util/sessionTimeout';
import withLogger from '../HOC/WithLogger';
import CustomerServiceCard from './CustomerServiceCard';

type VerifyContactCustomerServiceProps = {
    log: ApiRequestLogger;
    message: string;
};

function VerifyContactCustomerService({ log, message }: VerifyContactCustomerServiceProps) {
    // useAccessToken() is safe to use in unauthenticated pages. When unauthenticated, it ignores the user tracking events.
    const { accessToken } = useAccessToken();

    const searchParams = new URLSearchParams(document.location.search);
    const isMobile = searchParams.get(OPENID_PARAMS.MOBILE) === 'true';
    const clientLandingPageUrl = searchParams.get(OPENID_PARAMS.CANCEL_REDIRECT_URI);
    const { instance } = useMsal();
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const validRedirectUrl = getValidatedRedirectUrl(clientLandingPageUrl, clientConfiguration.redirectAllowedList);
    useErrorLogging(log);

    useEffect(() => {
        window.scrollTo(0, 0);
        /* API call to log the error message, if one is provided */
        if (message) {
            log.info(message);
        }
    }, [message]);

    useEffect(() => {
        if (accessToken) {
            const eventProps = buildIdVerificationEventProps(AmplitudeEventType.IdentityVerification_ContactService, 'sms');
            sendEvent(eventProps, accessToken);
        }
    }, [accessToken]);

    const handleLinkClick = () => {
        if (accessToken) {
            const eventProps = buildPhoneNumberEventProps(
                AmplitudeEventType.IdentityVerification_ContactService_Call,
                'sms',
                formatPhoneNumber(clientConfiguration.customerSupportNumber),
            );
            sendEvent(eventProps, accessToken);
        }
    };

    const handleCloseButtonClick = () => {
        if (accessToken) {
            const eventProps = buildIdVerificationEventProps(AmplitudeEventType.IdentityVerification_ContactService_Close, 'sms');
            sendEvent(eventProps, accessToken);
        }
        redirectWithSoftLogout(instance, validRedirectUrl, validateRedirectUrl);
    };

    if (!isMobile && validRedirectUrl) {
        return <CustomerServiceCard closeUrl={validRedirectUrl} onClose={handleCloseButtonClick} onLinkClick={handleLinkClick} variant='closable' />;
    } else {
        // Mobile will provide a back button for the user to touch, don't display the Close button
        return <CustomerServiceCard onLinkClick={handleLinkClick} variant='default' />;
    }
}

export default withLogger(VerifyContactCustomerService, { eventSource: 'VerifyContactCustomerService' });
